/**
 * HTML 의 보일 영역 고르기(2026-11-03, 반반 화면의 HTML 칸) — 고정 크기로 만든 HTML 위에서 끌어 네모를 그리면 그 영역이 칸에
 * 들어오게 키워 보인다(`@signageone/core` 의 `htmlAreaBox`). 1920×1080 현황판의 한 부분만 반쪽 칸에 크게 보일 때.
 *
 * 값은 기준 크기에 대한 비율(0~1)이라 HTML 을 다시 재어 기준 크기가 조금 바뀌어도 같은 부분을 가리킨다. 너무 작은 네모(5% 밑)는
 * 실수로 누른 것으로 보고 버린다. [전체] 로 비운다.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { HtmlArea } from '@signageone/core';
import { HtmlFrame } from '../HtmlContent.js';

type Size = { width: number; height: number };

const MIN = 0.05;
const clamp = (value: number): number => Math.min(1, Math.max(0, value));
const round = (value: number): number => Math.round(value * 1000) / 1000;

/** 끈 두 점으로 영역을 — 너무 작으면 null. */
export function areaFromDrag(start: { x: number; y: number }, end: { x: number; y: number }): HtmlArea | null {
  const x = clamp(Math.min(start.x, end.x));
  const y = clamp(Math.min(start.y, end.y));
  const w = clamp(Math.max(start.x, end.x)) - x;
  const h = clamp(Math.max(start.y, end.y)) - y;
  if (w < MIN || h < MIN) return null;
  return { x: round(x), y: round(y), w: round(Math.min(w, 1 - round(x))), h: round(Math.min(h, 1 - round(y))) };
}

export function HtmlAreaPicker({
  url,
  title,
  design,
  area,
  disabled,
  onChange,
}: {
  url: string;
  title: string;
  design: Size;
  area: HtmlArea | null;
  disabled: boolean;
  onChange: (next: HtmlArea | null) => void;
}): ReactNode {
  const surface = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ start: { x: number; y: number }; end: { x: number; y: number } } | null>(null);

  const point = (event: ReactPointerEvent): { x: number; y: number } => {
    const rect = surface.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return { x: clamp((event.clientX - rect.left) / rect.width), y: clamp((event.clientY - rect.top) / rect.height) };
  };

  const shown = drag ? areaFromDrag(drag.start, drag.end) : area;
  return (
    <div className="html-area">
      <div
        className="html-area__stage"
        style={{ aspectRatio: `${design.width} / ${design.height}` }}
        aria-label={`${title} — 끌어서 보일 영역 고르기`}
      >
        <HtmlFrame url={url} title={title} className="h-full w-full" screen={design} design={design} fit="contain" />
        <div
          ref={surface}
          className={`html-area__surface${disabled ? ' is-disabled' : ''}`}
          onPointerDown={(event) => {
            if (disabled) return;
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const at = point(event);
            setDrag({ start: at, end: at });
          }}
          onPointerMove={(event) => {
            if (drag) setDrag({ ...drag, end: point(event) });
          }}
          onPointerUp={(event) => {
            if (!drag) return;
            const next = areaFromDrag(drag.start, point(event));
            setDrag(null);
            if (next) onChange(next);
          }}
          onPointerCancel={() => setDrag(null)}
        >
          {shown ? (
            <span
              className="html-area__box"
              style={{
                left: `${shown.x * 100}%`,
                top: `${shown.y * 100}%`,
                width: `${shown.w * 100}%`,
                height: `${shown.h * 100}%`,
              }}
            />
          ) : null}
        </div>
      </div>
      <div className="field-row mt-1.5 items-center">
        <span className="hint">
          {area
            ? `고른 영역 — 왼쪽 ${Math.round(area.x * 100)}% · 위 ${Math.round(area.y * 100)}% · 폭 ${Math.round(area.w * 100)}% · 높이 ${Math.round(area.h * 100)}%`
            : '전체가 보입니다 — 끌어서 네모를 그리면 그 부분이 칸에 크게 나옵니다.'}
        </span>
        {area ? (
          <button type="button" className="btn btn-tiny" disabled={disabled} onClick={() => onChange(null)}>
            전체
          </button>
        ) : null}
      </div>
    </div>
  );
}
